import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';

const API = environment.api + 'data-exchange';

export interface ExchangeField {
    key: string;
    label: string;
    type: string;
    import: boolean;
    required: boolean;
    options: string[] | null;
}

export interface DataSet {
    key: string;
    label: string;
    import: boolean;
    can_add: boolean;       // false: existing records only (every row needs an ID)
    date_label: string;
    fields: ExchangeField[];
}

export interface ImportRow {
    line: number;
    values: { [fieldKey: string]: string };
}

export interface RowResult {
    line: number;
    id: string;
    status: 'new' | 'overwrite' | 'unchanged' | 'error';
    errors: string[];
    changes: { field: string, from: string, to: string }[];
}

export interface ImportLog {
    id: number;
    data_set: string;
    file_name: string;
    user: string;
    total_rows: number;
    created: number;
    updated: number;
    unchanged: number;
    skipped: number;
    date: string;
}

/**
 * Settings > Data Export / Import (api/data-exchange, administrators only). The API maps field
 * keys to real columns (config/data_exchange.php); this side only knows labels. Nothing is stored:
 * exports download straight to the browser, imports send at most `batch` parsed rows per call.
 */
@Injectable({ providedIn: 'root' })
export class DataExchangeService {
    constructor(private http: HttpClient) { }

    catalog(): Observable<{ data_sets: DataSet[], batch: number }> {
        return this.http.get<any>(API);
    }

    logs(): Observable<{ logs: ImportLog[] }> {
        return this.http.get<any>(API + '/logs');
    }

    export(set: string, fields: string[], from: string, to: string): Observable<HttpResponse<Blob>> {
        return this.http.post(API + '/' + set + '/export', { fields, from: from || null, to: to || null },
            { observe: 'response', responseType: 'blob' });
    }

    validate(set: string, rows: ImportRow[]): Observable<{ rows: RowResult[] }> {
        return this.http.post<any>(API + '/' + set + '/validate', { rows });
    }

    import(set: string, rows: ImportRow[], fileName: string, totalRows: number, logId: number):
        Observable<{ log_id: number, rows: RowResult[], counts: { created: number, updated: number, unchanged: number, skipped: number } }> {
        return this.http.post<any>(API + '/' + set + '/import', { rows, file_name: fileName, total_rows: totalRows, log_id: logId || null });
    }

    /** Saves a Blob as a file (the export, or the error rows). */
    save(blob: Blob, fileName: string) {
        const nav: any = window.navigator;
        if (nav.msSaveOrOpenBlob) {
            nav.msSaveOrOpenBlob(blob, fileName);
            return;
        }
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setTimeout(() => URL.revokeObjectURL(url), 1000);
    }

    /** RFC 4180 CSV: quoted cells, doubled quotes, line breaks inside quotes, BOM, CRLF / LF. */
    parseCsv(text: string): string[][] {
        const rows: string[][] = [];
        let row: string[] = [];
        let cell = '';
        let quoted = false;
        const src = text.charCodeAt(0) === 0xFEFF ? text.slice(1) : text;
        for (let i = 0; i < src.length; i++) {
            const c = src[i];
            if (quoted) {
                if (c === '"') {
                    if (src[i + 1] === '"') {
                        cell += '"';
                        i++;
                    } else {
                        quoted = false;
                    }
                } else {
                    cell += c;
                }
            } else if (c === '"') {
                quoted = true;
            } else if (c === ',') {
                row.push(cell);
                cell = '';
            } else if (c === '\n' || c === '\r') {
                if (c === '\r' && src[i + 1] === '\n') {
                    i++;
                }
                row.push(cell);
                rows.push(row);
                row = [];
                cell = '';
            } else {
                cell += c;
            }
        }
        if (cell !== '' || row.length) {
            row.push(cell);
            rows.push(row);
        }
        // Drop blank lines (e.g. the end of the file).
        return rows.filter(r => r.some(v => v.trim() !== ''));
    }

    toCsv(rows: string[][]): string {
        return rows.map(r => r.map(v => {
            const text = v === null || v === undefined ? '' : String(v);
            return /[",\r\n]/.test(text) ? '"' + text.replace(/"/g, '""') + '"' : text;
        }).join(',')).join('\r\n');
    }
}
